import type { TFunction } from 'i18next';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { uuid } from '@/helpers/common';
import { createSelectors } from '../helpers';

const TOOLTIP_REMOVE_TIMEOUT = 7000;

export enum TooltipTypes {
  SUCCESS = 0,
  WARNING = 1,
  INFO = 2,
  ERROR = 3,
}

interface BaseTooltipConfig {
  type: TooltipTypes;
  id?: string;
  animate?: boolean;
  removeTimeout?: number;
  pauseOnHover?: boolean;
}

export interface TooltipConfig extends BaseTooltipConfig {
  body: React.ReactNode;
}

export interface TooltipConfigWithTranslation extends BaseTooltipConfig {
  body: ((t: TFunction) => string) | string;
  translate: boolean;
}

interface TooltipI extends BaseTooltipConfig {
  body: string | React.ReactNode;
  translate?: boolean;
}

export interface TooltipState {
  tooltip: TooltipI | undefined;

  show(config: TooltipConfigWithTranslation | TooltipConfig): void;

  remove: () => void;
}

const useTooltipStoreBase = create(
  immer<TooltipState>((set) => ({
    tooltip: undefined,

    show: ({
      type,
      body,
      id,
      animate = true,
      translate = false,
      removeTimeout = TOOLTIP_REMOVE_TIMEOUT,
      pauseOnHover = false,
    }: TooltipI) =>
      set({
        tooltip: {
          id: id == null ? uuid() : id,
          type,
          body,
          animate,
          translate,
          removeTimeout,
          pauseOnHover,
        },
      }),

    remove: () => set({ tooltip: undefined }),
  }))
);

export const useTooltipStore = createSelectors(useTooltipStoreBase);
